import Layout from 'antd/lib/layout'
import Result from 'antd/lib/result'
import BackTop from 'antd/lib/back-top'
import React, { useState, useEffect, Suspense, lazy } from 'react'
import { Route, useLocation, Switch, Redirect } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import Home from '../Home'
import Orders from '../Orders'
import Sidebar from './Sidebar'
import Loyalty from '../Loyalty'
import Settings from '../Settings'
import Locations from '../Locations'
import Customers from '../Customers'
import Discounts from '../Discounts'
import NotFound from '../Misc/NotFound'
import HelpCentre from '../Home/HelpCentre'
import PushNotificationCampaigns from '../AppCampaigns'
import Composer from './../Composer'
import Stream from './../Stream'
import OrdersPending from '../Orders/OrdersPending'
import LocationForm from '../Locations/LocationForm'
import OrdersCalendar from '../Orders/OrdersCalendar'
import FeatureNotFound from '../Misc/FeatureNotFound'
import TermsAndConditions from './TermsAndConditions'
import PrintOrder from 'components/Orders/PrintOrder'
import LocationDashboard from '../Locations/Dashboard/'
import ProductsAndModifiers from '../ProductsAndModifiers'
import MarketingInitiativeModal from '../MarketingAndSales/InitiativeModal'
import PricingTable from '../Locations/PreOrderSettings/Delivery/PricingTable'
import { useSession, signOut, isAdmin, User } from '@slerp/accounts'
import { default as LocationSettings } from '../Locations/Settings'
// import UserFlow from './../Utils/Userflow'
import { useHubspotChat } from '../../hooks/useHubspotChat'
import { CREATE_MERCHANT_SUBSCRIPTION } from './actions'
// import moment from 'moment'
import { StampLogs } from './StampLogs'
import StripeRedirectRoute from './StripeRedirectRoute'
import { usePageViewTracker } from 'packages/@slerp/tracker'
import Loading from 'components/Utils/Loading'
import { useGlobalQuery } from 'hooks/useGlobalQuery'
import LayoutTitle from './LayoutTitle'
import LayoutContent from './LayoutContent'
import styled from '@emotion/styled'
import { ExpiredCardWarning } from '../Common'
import { COLLAPSED_MARGIN_LEFT, EXPANDED_MARGIN_LEFT } from './constants'

const SelectCreateDiscount = lazy(
  () => import('components/Discounts/SelectCreateDiscount')
)
const SlerpDesignerRework = lazy(() => import('../../AppDesignerRework'))
const AvailabilityRules = lazy(() => import('./../Locations/AvailabilityRules'))
const CategoryCutOffForm = lazy(
  () =>
    import('../Locations/AvailabilityRules/CategoryCutOffs/CategoryCutOffForm')
)
const SelectCreateRules = lazy(
  () =>
    import('../Locations/AvailabilityRules/CategoryCutOffs/SelectCreateRules')
)
const SpecialAvailabilityForm = lazy(
  () =>
    import(
      '../Locations/AvailabilityRules/SpecialAvailability/SpecialAvailabilityForm'
    )
)

const DiscountForm = lazy(
  () => import('components/Discounts/DiscountForm/DiscountForm')
)

export interface PageHeaderType {
  title: string
  tooltipTitle?: React.ReactNode
}

export interface CardType {
  id: string
  last4: number
  expMonth: number
  expYear: number
  expired?: boolean
  defaultSource: string
}

const AppContainer = () => {
  usePageViewTracker()
  const { merchant, user } = useSession()
  const {
    hasExpiredDefaultCard,
    // onboardingStatus,
    merchantSetting,
    errorMerchantSetting,
    collapsed
  } = useGlobalQuery()
  const [pageHeader, setPageHeader] = useState<PageHeaderType | null>(null)
  const [displayMarketingModal, setDisplayMarketingModal] =
    useState<boolean>(false)
  const location = useLocation()
  const pathContext = location?.pathname?.split('/').slice(1, 2)[0]
  const isPrintOrder = pathContext === 'print-order'

  // const { initUserFlow, userflow, safeOpenResourceCenter } = UserFlow()
  const hubspotJWT = useHubspotChat(user as User)

  const [createSubscription] = useMutation(CREATE_MERCHANT_SUBSCRIPTION, {
    fetchPolicy: 'no-cache',
    refetchQueries: ['getStripeData']
  })

  // const isOnboarding = merchantSetting?.has_onboarded
  // const isIdentified = userflow.isIdentified()

  // Separate Userflow initialization (no dependency on Hubspot)
  // useEffect(() => {
  //   if (isPrintOrder) return
  //   if (!merchantSetting) return

  //   initUserFlow({
  //     user: user as User,
  //     hasOnboarded: isOnboarding,
  //     onboardingStatus: onboardingStatus,
  //     isAdmin: isAdmin(user),
  //     companyName: merchantSetting.name
  //   })
  // }, [onboardingStatus, isOnboarding, user?.inserted_at, merchantSetting])

  // Separate Hubspot chat initialization
  useEffect(() => {
    if (isPrintOrder) return
    if (!hubspotJWT) return

    const identifyAndLoad = () => {
      ;(window as any).hsConversationsSettings = {
        loadImmediately: false,
        identificationEmail: user?.email,
        identificationToken: hubspotJWT
      }

      // If already loaded from a prior run, reset and remove before reloading

      if ((window as any).HubSpotConversations?.widget?.isLoaded?.()) {
        ;(window as any).HubSpotConversations.clear?.({ resetWidget: true })
        ;(window as any).HubSpotConversations.widget.remove?.()
      }

      ;(window as any).HubSpotConversations?.widget?.load?.()
    }

    if ((window as any).HubSpotConversations) {
      identifyAndLoad()
    } else {
      const t = setInterval(() => {
        if ((window as any).HubSpotConversations) {
          clearInterval(t)
          identifyAndLoad()
        }
      }, 200)
      return () => clearInterval(t)
    }
  }, [hubspotJWT, user?.email])

  // useEffect(() => {
  //   if (isPrintOrder) return // Skip Resource Center for print orders
  //   if (!isIdentified) return
  //   const userInsertedAt = moment(user?.inserted_at)
  //   const fourteenDaysAgo = moment().subtract(14, 'days')
  //   const timer = setTimeout(() => {
  //     if (userInsertedAt.isAfter(fourteenDaysAgo)) {
  //       safeOpenResourceCenter()
  //     }
  //   }, 7500)

  //   return () => clearTimeout(timer)
  // }, [isIdentified, isPrintOrder])

  useEffect(() => {
    createSubscription({
      variables: {
        merchant_id: merchant?.id
      }
    }).catch((error) =>
      console.error(`Unable to create subscription due to ${error}`, 5)
    )
  }, [])

  // Check JWT validity. If invalid, sign out.
  useEffect(() => {
    if (errorMerchantSetting?.graphQLErrors) {
      const [err] = errorMerchantSetting.graphQLErrors
      if (err && err?.extensions?.code === 'invalid-jwt') {
        signOut()
      }
    }

    if (merchantSetting) {
      const { marketing_modal_viewers } = merchantSetting
      setDisplayMarketingModal(
        !(marketing_modal_viewers || []).includes(user?.id || '')
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantSetting, errorMerchantSetting?.graphQLErrors])

  const isAvailabilityRules = location?.pathname?.match(
    /\/availability-rules(?:\/|$)/
  )

  useEffect(() => {
    switch (pathContext) {
      case '':
        document.title = 'Slerp Controls - Insights'
        setPageHeader({
          title: 'Insights',
          tooltipTitle: (
            <span>
              All your insights on this dashboard can be filtered by your
              store's locations and time period.
            </span>
          )
        })
        break
      case 'analytics':
        document.title = 'Slerp Controls - Insights'
        setPageHeader({
          title: 'Insights',
          tooltipTitle: (
            <span>
              All your insights on this dashboard can be filtered by your
              store's locations and time period.
            </span>
          )
        })
        break
      case 'heat-map':
        document.title = 'Slerp Controls - Insights'
        setPageHeader({
          title: 'Insights',
          tooltipTitle: (
            <span>
              All your insights on this dashboard can be filtered by your
              store's locations and time period.
            </span>
          )
        })
        break
      case 'settings':
        document.title = 'Slerp Controls - Settings'
        setPageHeader({
          title: 'Settings',
          tooltipTitle: (
            <span>
              Here's where you can edit the master settings for your brand's
              Slerp account.
            </span>
          )
        })
        break
      case 'orders':
        document.title = 'Slerp Controls - Orders'
        setPageHeader({
          title: 'Orders',
          tooltipTitle: (
            <span>
              A breakdown of all placed and pending orders which can be filtered
              by store, fulfilment type and date range.
            </span>
          )
        })
        break
      case 'location':
        if (isAvailabilityRules) {
          document.title = 'Slerp Controls - Locations - Availability Rules'
          setPageHeader({
            title: 'Availability Rules'
          })

          break
        } else {
          document.title = 'Slerp Controls - Locations'
          setPageHeader({
            title: 'Locations',
            tooltipTitle: (
              <span>
                An{' '}
                <a
                  href='https://support.slerp.com/en_gb/stores-and-locations-rkkCUtEpd'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  overview
                </a>{' '}
                of your
                <br />
                stores' set up.
              </span>
            )
          })
          break
        }
      case 'locations':
        document.title = 'Slerp Controls - Locations'
        setPageHeader({
          title: 'Locations',
          tooltipTitle: (
            <span>
              An{' '}
              <a
                href='https://support.slerp.com/en_gb/stores-and-locations-rkkCUtEpd'
                target='_blank'
                rel='noopener noreferrer'
              >
                overview
              </a>{' '}
              of your
              <br />
              stores' set up.
            </span>
          )
        })
        break
      case 'products':
        document.title = 'Slerp Controls - Products'
        setPageHeader({
          title: 'Products',
          tooltipTitle: (
            <span>
              <a
                href='https://support.slerp.com/en_gb/categories/products-H1ZdJ7G6u'
                target='_blank'
                rel='noopener noreferrer'
              >
                Here's our guides
              </a>{' '}
              for creating and managing your products. See this as your master
              library of producs that you then assign to each of your stores'
              inventories.
            </span>
          )
        })
        break
      case 'modifiers':
        document.title = 'Slerp Controls - Modifiers'
        setPageHeader({
          title: 'Modifiers',
          tooltipTitle: (
            <span>
              Modifiers are a powerful upselling tool. They allow a product to
              have multiple add ons.
              <br />
              <br />
              Find out more{' '}
              <a
                href='https://support.slerp.com/knowledge/modifiers'
                target='_blank'
                rel='noopener noreferrer'
              >
                here
              </a>
              .
            </span>
          )
        })
        break
      case 'discounts':
        document.title = 'Slerp Controls - Discounts'
        setPageHeader({
          title: 'Discounts',
          tooltipTitle: (
            <span>
              <a
                href='https://support.slerp.com/knowledge/creating-and-managing-discount-codes'
                target='_blank'
                rel='noopener noreferrer'
              >
                Discount codes
              </a>{' '}
              can be used to increase conversion, drive loyalty and win back
              customers.
              <br />
              <br />
              Did you know one of the biggest barriers to online sales is
              delivery fees?
              <br />
              <br /> Why not try a free delivery campaign to drive sales on your
              new product?
            </span>
          )
        })
        break
      case 'customers':
        document.title = 'Slerp Controls - Customers'
        setPageHeader({
          title: 'Customers',
          tooltipTitle: (
            <span>
              One of the biggest advantages of selling direct to customers
              online is you have{' '}
              <a
                href='https://support.slerp.com/en_gb/download-customer-database-SyA9W_qt'
                target='_blank'
                rel='noopener noreferrer'
              >
                access to their data
              </a>{' '}
              for your marketing efforts.
              <br />
              <br />
              Here you'll be able to see and download your customers' details
              and order frequencies. Talk to member of our team about CRM
              integrations.
            </span>
          )
        })
        break
      case 'designer':
        document.title = 'Slerp Controls - Designer'
        setPageHeader({
          title: 'Designer',
          tooltipTitle: (
            <span>
              A seamless, branded experience from order button to checkout
              drives trust which in turns drives sales.
              <br />
              <br />
              Designer enables you to fully brand and add character to your
              Slerp storefront.
              <br />
              <br />
              Learn how to optimise your store{' '}
              <a
                href='http://slerp-8156030.hs-sites.com/optimise'
                target='_blank'
                rel='noopener noreferrer'
              >
                here
              </a>
              .
            </span>
          )
        })
        break
      case 'composer':
        document.title = 'Slerp Controls - Composer'
        setPageHeader({ title: 'Composer' })
        break
      case 'slerp-menu-manager':
        document.title = 'Slerp Controls - Menu Manager'
        setPageHeader({ title: 'Menu Manager' })
        break
      case 'loyalty':
        document.title = 'Slerp Controls - Loyalty'
        setPageHeader({ title: 'Loyalty' })
        break
      case 'app-campaigns':
        document.title = 'Slerp Controls - App Campaigns'
        setPageHeader({ title: 'App Campaigns' })
        break
      default:
        document.title = 'Slerp Controls'
        setPageHeader(null)
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const Pending = () => (
    <Result
      status='info'
      title='This page is coming soon'
      subTitle='Sorry, but we are still working on this page. Please come back later.'
    />
  )

  if (pathContext === 'print-order')
    return <Route path='/print-order/:order_id' exact component={PrintOrder} />

  return (
    <StyledLayout hasSider collapsed={collapsed}>
      <Sidebar />
      <StampLogs />
      <Layout className='site-layout'>
        <LayoutTitle pageHeader={pageHeader} />

        {displayMarketingModal &&
          (location.pathname === '/' || location.pathname === '/analytics') && (
            <MarketingInitiativeModal />
          )}
        <ExpiredCardWarning show={!!hasExpiredDefaultCard} />
        <LayoutContent>
          <Suspense fallback={<Loading />}>
            <Switch>
              <Route path='/' exact>
                <Redirect to='/analytics' />
              </Route>
              <Route path='/analytics' exact component={Home} />
              <Route path='/heat-map' exact component={Home} />
              <Route path='/pending' component={OrdersPending} />
              <Route path='/calendar' exact component={OrdersCalendar} />
              <Route path='/orders' exact component={Orders} />
              <Route
                path='/orders/generate-report/:reportTab'
                exact
                component={Orders}
              />
              <Route path='/orders/:transactionId' exact component={Orders} />
              <Route path='/designer' exact>
                <Redirect to='/designer/web/landing' />
              </Route>
              <Route
                path='/designer/:tab/:subtab'
                exact
                component={SlerpDesignerRework}
              />
              <Route path='/designer/:tab' component={SlerpDesignerRework} />
              <Route path='/products' exact component={ProductsAndModifiers} />
              <Route
                path='/products/categories'
                exact
                component={ProductsAndModifiers}
              />
              <Route
                path='/products/categories/new'
                exact
                component={ProductsAndModifiers}
              />
              <Route
                path='/products/new'
                exact
                component={ProductsAndModifiers}
              />
              <Route
                path='/products/reorder'
                exact
                component={ProductsAndModifiers}
              />
              <Route path='/modifiers' exact component={ProductsAndModifiers} />
              <Route
                path='/modifiers/modifier-groups'
                exact
                component={ProductsAndModifiers}
              />
              <Route
                path='/modifiers/modifier-groups/new'
                exact
                component={ProductsAndModifiers}
              />
              <Route
                path='/modifiers/new'
                exact
                component={ProductsAndModifiers}
              />

              <Route
                path='/discounts/create'
                exact
                component={SelectCreateDiscount}
              />
              <Route
                path='/discounts/product/create'
                exact
                component={DiscountForm}
              />
              <Route
                path='/discounts/delivery-charge/create'
                exact
                component={DiscountForm}
              />
              <Route
                path='/discounts/basket/create'
                exact
                component={DiscountForm}
              />
              <Route
                path='/discounts/basket/:discount_id'
                exact
                component={DiscountForm}
              />
              <Route
                path='/discounts/product/:discount_id'
                exact
                component={DiscountForm}
              />
              <Route
                path='/discounts/delivery-charge/:discount_id'
                exact
                component={DiscountForm}
              />
              <Route
                path='/discounts/code/edit/:discount_id'
                exact
                component={Discounts}
              />
              <Route path='/discounts/manual' exact component={Discounts} />
              <Route path='/discounts/automatic' exact component={Discounts} />
              <Route path='/discounts/rewards' exact component={Discounts} />
              <Route path='/discounts' exact component={Discounts} />
              <Route
                path='/customers/customer-list'
                exact
                component={Customers}
              />
              <Route path='/customers/guest-list' exact component={Customers} />
              <Route path='/customers' exact component={Customers} />
              <Route
                path='/loyalty/overview/bulk-actions'
                exact
                component={Loyalty}
              />
              <Route path='/loyalty/overview' exact component={Loyalty} />
              <Route path='/loyalty/cards/new' exact component={Loyalty} />
              <Route path='/loyalty/cards' exact component={Loyalty} />
              <Route path='/loyalty/bonuses/new' exact component={Loyalty} />
              <Route path='/loyalty/bonuses' exact component={Loyalty} />
              <Route path='/loyalty'>
                <Loyalty />
              </Route>
              <Route path='/locations' exact component={Locations} />
              <Route path='/locations/new' component={LocationForm} />
              <StripeRedirectRoute path='/locations/stripe/connect' />
              <Route
                path='/location/:slug/availability-rules/create'
                exact
                component={() => (
                  <SelectCreateRules pathname={location?.pathname} />
                )}
              />
              <Route
                path='/location/:slug/availability-rules'
                exact
                component={AvailabilityRules}
              />
              <Route
                path='/location/:slug/availability-rules/category-cut-off/rule/:rule_id'
                component={() => (
                  <CategoryCutOffForm pathname={location?.pathname} />
                )}
              />
              <Route
                path='/location/:slug/availability-rules/category-cut-off/create'
                exact
                component={() => (
                  <CategoryCutOffForm pathname={location?.pathname} />
                )}
              />
              <Route
                path='/location/:slug/availability-rules/special-availability/rule/:rule_id'
                component={() => (
                  <SpecialAvailabilityForm pathname={location?.pathname} />
                )}
              />
              <Route
                path='/location/:slug/availability-rules/special-availability/create'
                exact
                component={() => (
                  <SpecialAvailabilityForm pathname={location?.pathname} />
                )}
              />
              <Route
                path='/location/:slug/orders'
                exact
                component={LocationDashboard}
              />
              <Route
                path='/location/:slug/inventory/same-day'
                exact
                component={LocationDashboard}
              />
              <Route
                path='/location/:slug/inventory/same-day/edit/:tab'
                exact
                component={LocationDashboard}
              />
              <Route
                path='/location/:slug/inventory/pre-orders'
                exact
                component={LocationDashboard}
              />
              <Route
                path='/location/:slug/inventory/pre-orders/edit/:tab'
                exact
                component={LocationDashboard}
              />
              <Route
                path='/location/:slug/settings'
                component={LocationSettings}
              />
              <Route
                path='/location/:slug/settings/general/:subtab'
                component={LocationSettings}
              />
              <Route
                path='/location/:slug/settings/same-day/:subtab'
                component={LocationSettings}
              />
              <Route
                path='/location/:slug/settings/pre-order/:subtab'
                component={LocationSettings}
              />
              <Route
                path='/location/:slug/settings/table/:subtab'
                component={LocationSettings}
              />
              <Route
                path='/location/:slug/slerp-optimize-delivery-pricing/:id'
                component={PricingTable}
              />
              <Route path='/settings' component={() => <Settings />} />
              <Route
                path='/app-campaigns/history'
                exact
                component={() =>
                  merchantSetting?.app_setting?.enabled ? (
                    <PushNotificationCampaigns />
                  ) : (
                    <FeatureNotFound />
                  )
                }
              />
              <Route
                path='/app-campaigns/scheduled'
                exact
                component={() =>
                  merchantSetting?.app_setting?.enabled ? (
                    <PushNotificationCampaigns />
                  ) : (
                    <FeatureNotFound />
                  )
                }
              />
              <Route
                path='/app-campaigns/new'
                exact
                component={() =>
                  merchantSetting?.app_setting?.enabled ? (
                    <PushNotificationCampaigns />
                  ) : (
                    <FeatureNotFound />
                  )
                }
              />
              <Route
                path='/app-campaigns'
                exact
                component={() =>
                  merchantSetting?.app_setting?.enabled ? (
                    <PushNotificationCampaigns />
                  ) : (
                    <FeatureNotFound />
                  )
                }
              />
              <Route path='/account' component={Pending} />
              <Route path='/help-centre' component={HelpCentre} />
              <Route
                path='/sign-out'
                component={() => {
                  signOut()
                  return <></>
                }}
              />
              <Route path='/composer' exact component={Composer} />
              <Route path='/slerp-menu-manager' exact component={Stream} />
              <Route component={NotFound} />
            </Switch>
          </Suspense>

          <BackTop />
        </LayoutContent>
        <TermsAndConditions
          isAdmin={isAdmin(user)}
          merchantId={merchant?.id || ''}
          merchantName={merchant?.name || ''}
          registeredCompanyName={merchantSetting?.registered_company_name}
        />
      </Layout>
    </StyledLayout>
  )
}

const StyledLayout = styled(Layout)<{ collapsed: boolean }>`
  .ant-btn:not(.ant-btn-icon-only),
  .ant-btn.ant-btn-default:not(.ant-btn-icon-only) {
    display: flex;
    align-items: center;
  }

  .site-layout {
    margin-left: ${({ collapsed }) =>
      collapsed ? `${COLLAPSED_MARGIN_LEFT}px` : `${EXPANDED_MARGIN_LEFT}px`};
    transition: margin-left 0.2s;
  }
`

export default AppContainer
